import { Children, cloneElement, useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";

// A horizontal rail that drifts slowly on its own and can also be scrolled,
// flicked, or click-dragged by mouse and touch.
//
// Behaviour that matters:
//  - Seamless infinite loop: items rendered twice, wrapping at the halfway point.
//  - Reversible drift direction (forward or reverse).
//  - Drag-to-scroll with mouse on desktop (grab/grabbing cursor) and touch on mobile.
//  - Pauses drift on hover, during active drag, and for 1.8s after interaction.
//  - Image ghost-drag prevention so dragging is immediate and responsive.
const DRIFT_PIXELS_PER_SECOND = 22;

export const AutoScroller = ({ children, ariaLabel, reverse = false, speed = DRIFT_PIXELS_PER_SECOND }) => {
    const railRef = useRef(null);
    const reduceMotion = useReducedMotion();
    const [isPaused, setIsPaused] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    const isPointerDown = useRef(false);
    const startX = useRef(0);
    const startScrollLeft = useRef(0);
    const resumeTimer = useRef(null);

    // Duplicate children for seamless infinite scroll
    const items = Children.toArray(children);
    const duplicates = items.map((child, index) =>
        cloneElement(child, { key: `loop-copy-${index}` }),
    );

    // Set initial scroll offset if reverse
    useEffect(() => {
        const rail = railRef.current;
        if (rail && reverse && rail.scrollLeft === 0) {
            const half = rail.scrollWidth / 2;
            if (half > 0) rail.scrollLeft = half;
        }
    }, [reverse]);

    // Continuous auto-drifting loop
    useEffect(() => {
        const rail = railRef.current;
        if (!rail || reduceMotion || isPaused || isDragging) return undefined;

        let frame;
        let last = null;
        const velocity = reverse ? -speed : speed;

        const step = (now) => {
            if (last === null) last = now;
            const elapsed = (now - last) / 1000;
            last = now;

            const half = rail.scrollWidth / 2;
            let next = rail.scrollLeft + velocity * elapsed;
            if (half > 0) {
                if (next >= half) next -= half;
                if (next < 0) next += half;
            }
            rail.scrollLeft = next;

            frame = requestAnimationFrame(step);
        };

        frame = requestAnimationFrame(step);
        return () => cancelAnimationFrame(frame);
    }, [reduceMotion, isPaused, isDragging, reverse, speed]);

    // Handle mouse click-and-drag & touch interactions
    const onMouseDown = (e) => {
        const rail = railRef.current;
        if (!rail) return;
        isPointerDown.current = true;
        setIsDragging(true);
        setIsPaused(true);
        clearTimeout(resumeTimer.current);

        startX.current = e.pageX - rail.offsetLeft;
        startScrollLeft.current = rail.scrollLeft;
    };

    const onMouseMove = (e) => {
        if (!isPointerDown.current) return;
        e.preventDefault();
        const rail = railRef.current;
        if (!rail) return;

        const x = e.pageX - rail.offsetLeft;
        const walk = (x - startX.current) * 1.3;
        let newScrollLeft = startScrollLeft.current - walk;

        const half = rail.scrollWidth / 2;
        if (half > 0) {
            if (newScrollLeft >= half) {
                newScrollLeft -= half;
                startX.current = x;
                startScrollLeft.current = newScrollLeft;
            } else if (newScrollLeft < 0) {
                newScrollLeft += half;
                startX.current = x;
                startScrollLeft.current = newScrollLeft;
            }
        }

        rail.scrollLeft = newScrollLeft;
    };

    const onMouseUpOrLeave = () => {
        if (!isPointerDown.current) return;
        isPointerDown.current = false;
        setIsDragging(false);

        clearTimeout(resumeTimer.current);
        resumeTimer.current = setTimeout(() => {
            setIsPaused(false);
        }, 1800);
    };

    // Prevent default browser drag on images inside the rail
    useEffect(() => {
        const rail = railRef.current;
        if (!rail) return;

        const handleDragStart = (e) => e.preventDefault();
        rail.addEventListener("dragstart", handleDragStart);

        return () => {
            rail.removeEventListener("dragstart", handleDragStart);
            clearTimeout(resumeTimer.current);
        };
    }, []);

    return (
        <div
            ref={railRef}
            role="group"
            aria-label={ariaLabel}
            tabIndex={0}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => {
                if (!isPointerDown.current) {
                    setIsPaused(false);
                } else {
                    onMouseUpOrLeave();
                }
            }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUpOrLeave}
            onFocus={() => setIsPaused(true)}
            onBlur={() => setIsPaused(false)}
            className={`flex gap-[26px] overflow-x-auto pb-4 pt-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden select-none ${
                isDragging ? "cursor-grabbing" : "cursor-grab"
            }`}
        >
            {items}
            <div aria-hidden className="flex shrink-0 gap-[26px]">
                {duplicates}
            </div>
        </div>
    );
};
